/**
 * Unified feature catalog for internal linking.
 *
 * Single source of truth for all feature pages - used by the mega-menu,
 * footer, RelatedFeatures component, and FeaturesPage grid. Every slug here
 * must resolve to a data-driven page (featurePages.ts) or a dedicated landing
 * page route in App.tsx (enforced by featureCatalog integrity test).
 */

import {
  Clock,
  Gauge,
  TrendingUp,
  Rocket,
  RotateCcw,
  Flame,
  ShieldAlert,
  Lock,
  Network,
  Users,
  MessageSquare,
  Compass,
  Presentation,
  PieChart,
  Bell,
  Bot,
  BarChart3,
  Plug,
  Activity,
  Timer,
  Trophy,
  Heart,
} from 'lucide-react';

export type FeatureCategory = 'delivery' | 'quality' | 'team' | 'intelligence';

/** Shared icon map for resolving catalog icon strings to Lucide components. */
export const FEATURE_ICON_MAP: Record<string, React.ElementType> = {
  Clock,
  Gauge,
  TrendingUp,
  Rocket,
  RotateCcw,
  Flame,
  ShieldAlert,
  Lock,
  Network,
  Users,
  MessageSquare,
  Compass,
  Presentation,
  PieChart,
  Bell,
  Bot,
  BarChart3,
  Plug,
  Activity,
  Timer,
  Trophy,
  Heart,
};

export interface FeatureCatalogEntry {
  slug: string;
  title: string;
  shortDescription: string;
  icon: string; // Lucide icon name
  category: FeatureCategory;
}

export const FEATURE_CATEGORIES: Record<FeatureCategory, { label: string }> = {
  delivery: { label: 'Delivery & Velocity' },
  quality: { label: 'Code Quality & Risk' },
  team: { label: 'Team & Collaboration' },
  intelligence: { label: 'Intelligence & Reporting' },
};

export const featureCatalog: FeatureCatalogEntry[] = [
  // --- Delivery & Velocity ---
  {
    slug: 'cycle-time',
    title: 'PR Cycle Time Analytics',
    shortDescription: 'Break down cycle time into coding, waiting, review, and merge phases.',
    icon: 'Clock',
    category: 'delivery',
  },
  {
    slug: 'dora-metrics',
    title: 'DORA Metrics Dashboard',
    shortDescription: 'Track all four DORA metrics and benchmark against industry standards.',
    icon: 'Gauge',
    category: 'delivery',
  },
  {
    slug: 'forecasting',
    title: 'Delivery Forecasting',
    shortDescription: 'Predict delivery timelines based on historical velocity trends.',
    icon: 'TrendingUp',
    category: 'delivery',
  },
  {
    slug: 'deployment-analytics',
    title: 'Deployment Analytics',
    shortDescription: 'Track deployment frequency, lead time, and release patterns.',
    icon: 'Rocket',
    category: 'delivery',
  },
  {
    slug: 'sprint-retro',
    title: 'Sprint Retrospectives',
    shortDescription: 'Data-backed retros with concrete metrics for each sprint.',
    icon: 'RotateCcw',
    category: 'delivery',
  },
  {
    slug: 'velocity',
    title: 'Velocity Trends',
    shortDescription: 'See whether engineering is trending up or down with a Velocity Score across speed, throughput, quality, and collaboration.',
    icon: 'Activity',
    category: 'delivery',
  },
  {
    slug: 'issue-lead-time',
    title: 'Issue Lead Time',
    shortDescription: 'Measure end-to-end issue lead time from creation to merge and find where work stalls across Jira or Linear statuses.',
    icon: 'Timer',
    category: 'delivery',
  },

  // --- Code Quality & Risk ---
  {
    slug: 'file-hotspots',
    title: 'File Hotspot Analysis',
    shortDescription: 'Find high-churn files and correlate change frequency with ownership.',
    icon: 'Flame',
    category: 'quality',
  },
  {
    slug: 'risky-changes',
    title: 'Risky Change Detection',
    shortDescription: 'Flag large PRs, single-owner changes, and untested modifications.',
    icon: 'ShieldAlert',
    category: 'quality',
  },
  {
    slug: 'knowledge-silos',
    title: 'Knowledge Silo Detection',
    shortDescription: 'Detect bus-factor-1 code before it becomes an operational risk.',
    icon: 'Lock',
    category: 'quality',
  },

  // --- Team & Collaboration ---
  {
    slug: 'review-network',
    title: 'Review Network Visualization',
    shortDescription: 'Interactive graph of review relationships and workload distribution.',
    icon: 'Network',
    category: 'team',
  },
  {
    slug: 'developer-comparison',
    title: 'Developer Insights',
    shortDescription: 'Compare contribution patterns for coaching, not surveillance.',
    icon: 'Users',
    category: 'team',
  },
  {
    slug: 'review-insights',
    title: 'Review Quality & Sentiment',
    shortDescription: 'Measure review thoroughness and feedback quality across the team.',
    icon: 'MessageSquare',
    category: 'team',
  },
  {
    slug: 'space-framework',
    title: 'SPACE Framework Metrics',
    shortDescription: 'Holistic developer productivity using the SPACE framework.',
    icon: 'Compass',
    category: 'team',
  },
  {
    slug: 'developer-surveys',
    title: 'Developer Experience Surveys',
    shortDescription: 'Run recurring, anonymous developer experience surveys and read team sentiment next to your GitHub delivery data.',
    icon: 'Heart',
    category: 'team',
  },
  {
    slug: 'awards',
    title: 'Engineering Awards',
    shortDescription: 'Celebrate the healthy, often-invisible behaviours - careful reviews, mentorship, testing - that make teams stronger.',
    icon: 'Trophy',
    category: 'team',
  },

  // --- Intelligence & Reporting ---
  {
    slug: 'executive-summary',
    title: 'Executive Summary',
    shortDescription: 'Board-ready engineering performance in a single page.',
    icon: 'Presentation',
    category: 'intelligence',
  },
  {
    slug: 'investment-allocation',
    title: 'Investment Categorization',
    shortDescription: 'See where engineering effort goes across features, maintenance, and debt.',
    icon: 'PieChart',
    category: 'intelligence',
  },
  {
    slug: 'alerts',
    title: 'Smart Alerts',
    shortDescription: 'Get notified when cycle times spike or review patterns degrade.',
    icon: 'Bell',
    category: 'intelligence',
  },
  {
    slug: 'ai-tools-roi',
    title: 'AI Tools ROI',
    shortDescription: 'Measure the real productivity impact of AI coding assistants.',
    icon: 'Bot',
    category: 'intelligence',
  },
  {
    slug: 'benchmarks',
    title: 'Industry Benchmarks',
    shortDescription: 'See how your team\'s delivery metrics rank against similar companies by industry and size, using anonymized opt-in benchmarks.',
    icon: 'BarChart3',
    category: 'intelligence',
  },
  {
    slug: 'integrations',
    title: 'Jira, Linear & Azure DevOps',
    shortDescription: 'Connect Jira, Linear, and Azure DevOps so issue context enriches your GitHub-native analytics, with OAuth setup in minutes.',
    icon: 'Plug',
    category: 'intelligence',
  },
];

/** Look up a feature by slug. */
export function getFeatureCatalogEntry(slug: string): FeatureCatalogEntry | undefined {
  return featureCatalog.find((f) => f.slug === slug);
}

/** Get all features in a category. */
export function getFeaturesByCategory(category: FeatureCategory): FeatureCatalogEntry[] {
  return featureCatalog.filter((f) => f.category === category);
}

/** Get features grouped by category (preserves insertion order). */
export function getFeatureCatalogGrouped(): Record<FeatureCategory, FeatureCatalogEntry[]> {
  return {
    delivery: getFeaturesByCategory('delivery'),
    quality: getFeaturesByCategory('quality'),
    team: getFeaturesByCategory('team'),
    intelligence: getFeaturesByCategory('intelligence'),
  };
}

/** Resolve a catalog icon string to a Lucide component (falls back to Gauge). */
export function getFeatureIcon(iconName: string): React.ElementType {
  return FEATURE_ICON_MAP[iconName] || Gauge;
}
